import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./BookDetails.module.css";
import SimilarBooks from "./SimilarBooks";
import Footer from "../../../common/UI/Footer";
import { Book, Languages, LibraryBig, Plus, Heart, Check, BookOpen, Bookmark } from "lucide-react";
import { useChaptersBySeriesId } from "../../../hooks/useChapters";
import ContentNotFound from "../../../common/UI/ContentNotFound";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useEbookBySlug } from "../../../hooks/useEbooksHooks";
import {
  useCheckContentInPlaylist,
  useCreatePlaylist,
} from "../../../hooks/usePlayListHooks";
import {
  useLikeActions,
  useLikesCount,
  useLikeStatus,
} from "../../../hooks/useLikesHooks";
import useAuthStore from "../../../store/authStore";
import LoginModal from "../../../common/Authenticated/LoginModal";
import { useAddContinueListening } from "../../../hooks/useContinueListening";

const BookDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isInLibraryState, setIsInLibraryState] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginAction, setLoginAction] = useState(""); // To track which action initiated the login modal
  const { addContinueListening } = useAddContinueListening();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch ebook data using the slug from URL params
  const { ebook, isLoading: isEbookLoading, refetchEbook } = useEbookBySlug(id);
  const { data: likeStatusData } = useLikeStatus("ebook", ebook?.id);
  const { data: likesCountData } = useLikesCount("ebook", ebook?.id);
  const { likeItem, unlikeItem } = useLikeActions();

  // Fetch chapters data using the series ID
  const {
    series: seriesData,
    chapters,
    isLoading: isChaptersLoading,
  } = useChaptersBySeriesId(ebook?.series_id);

  // Check if the book is already in a playlist
  const {
    data: playlistCheck,
    isLoading: checkingPlaylist,
    refetch: refetchPlaylistCheck,
  } = useCheckContentInPlaylist(ebook?.id ? "ebook" : null, ebook?.id);

  // Create playlist mutation
  const { mutate: createPlaylist, isLoading: creatingPlaylist } =
    useCreatePlaylist();

  // Update local state when playlistCheck changes
  useEffect(() => {
    if (playlistCheck) {
      setIsInLibraryState(playlistCheck.inPlaylist);
    }
  }, [playlistCheck]);

  // Handle login button click
  const handleLogin = () => {
    setIsLoginModalOpen(false);
    // Redirect to login page
    navigate('/signin');
  };

  // Handle adding to library
  const handleAddToLibrary = () => {
    if (!isAuthenticated) {
      setLoginAction("library");
      setIsLoginModalOpen(true);
      return;
    }

    if (!ebook?.id || isAddingToLibrary) return;

    setIsAddingToLibrary(true);

    // Create a new playlist with this book
    createPlaylist(
      {
        item_type: "ebook",
        content_id: ebook.id,
        title: `${ebook.title} - Library`,
        description: `Library entry for ${ebook.title}`,
      },
      {
        onSuccess: () => {
          // Immediately update the UI state
          setIsInLibraryState(true);

          // Then refetch the actual data
          refetchPlaylistCheck();
        },
        onSettled: () => {
          setIsAddingToLibrary(false);
        },
      }
    );
  };

  // Handle like button click
  const handleLikeClick = () => {
    if (!isAuthenticated) {
      setLoginAction("like");
      setIsLoginModalOpen(true);
      return;
    }

    if (!ebook?.id) return;

    if (likeStatusData?.liked) {
      unlikeItem({
        item_type: "ebook",
        item_id: ebook.id,
      });
    } else {
      likeItem({
        item_type: "ebook",
        item_id: ebook.id,
      });
    }
  };

  // Handle read button click - Modified to handle different navigation based on has_chapters
  const handleReadClick = () => {
    if (!ebook) return;

    if (isAuthenticated) {
      addContinueListening({
        item_type: "ebook",
        item_id: ebook.id,
      });
    }

    // If book has chapters, navigate to the first chapter
    // Otherwise, navigate to the book content page directly
    if (ebook.has_chapters && chapters && chapters.length > 0) {
      navigate(
        `/book/${ebook.series_id}/episode/${chapters[0]?.ebook_slug}`,
        { state: { seriesId: ebook.series_id } }
      );
    } else {
      // For books without chapters
      navigate(`/book/${ebook.slug}`);
    }
  };

  const handleEpisodeClick = (index) => {
    if (!isAuthenticated) {
      setLoginAction("read");
      setIsLoginModalOpen(true);
      return;
    }
    
    if (chapters && chapters[index]) {
      navigate(
        `/book/${ebook.series_id}/episode/${chapters[index]?.ebook_slug}`
      );
    }
  };

  const likesCount = likesCountData?.count || 0;
  const isLiked = likeStatusData?.liked || false;

  if (isEbookLoading || isChaptersLoading || creatingPlaylist) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  if (!ebook) {
    return (
      <ContentNotFound message="Book Not Found" onRefresh={refetchEbook} />
    );
  }

  // Determine which cover image to use based on device
  const coverImage = isMobile
    ? ebook.cover_image_url_for_mbl
    : ebook.cover_image_url_for_computer;

  // Get login modal message based on the action
  const getLoginModalMessage = () => {
    switch (loginAction) {
      case "library":
        return "You have to login first to add this book to your library";
      case "like":
        return "You have to login first to like this book";
      case "read":
        return "You have to login first to read this book";
      default:
        return "You have to login first for this action";
    }
  };

  // Check if book has chapters to display the episodes section
  const hasChapters = ebook.has_chapters && chapters && chapters.length > 0;

  return (
    <>
      <div className={styles.main_container}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.coverWrapper}>
              <img
                src={
                  coverImage ||
                  ebook.cover_image_url_for_computer ||
                  ebook.cover_image_url_for_mbl
                }
                alt={ebook.title}
                className={styles.cover}
              />
            </div>
            <div className={styles.info}>
              <h1 className={styles.title}>{seriesData.title || ebook.title}</h1>
              <p className={styles.author}>
                {ebook?.language?.toLowerCase() === "hindi" ? "लेखक: " : "Author: "}
                {ebook.author}
              </p>
              
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <span className={styles.icon}>
                    <Book size={18} />
                  </span>
                  <span>{hasChapters ? seriesData?.total_chapters : "1"} {hasChapters ? "Episodes" : "Episode"}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.icon}>
                    <LibraryBig size={18} />
                  </span>
                  <span>{ebook.genre_name}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.icon}>
                    <Languages size={18} />
                  </span>
                  <span>{ebook.language}</span>
                </div>
                <div
                  className={`${styles.stat} ${styles.likeButton}`}
                  onClick={handleLikeClick}
                >
                  <Heart
                    className={`${styles.heartIcon} ${
                      isLiked ? styles.liked : ""
                    }`}
                    size={18}
                    fill={isLiked ? "#e50914" : "none"}
                    stroke={isLiked ? "#e50914" : "currentColor"}
                  />
                  <span>{likesCount}</span>
                </div>
              </div>
              
              <div className={styles.buttonGroup}>
                <button
                  className={styles.readButton}
                  onClick={handleReadClick}
                >
                  <BookOpen size={18} />
                  {ebook?.language?.toLowerCase() === "hindi" ? "अभी पढ़ें" : "Read Now"}
                </button>
                
                <button
                  className={`${styles.addLibrarybutton} ${
                    isInLibraryState ? styles.inLibrary : ""
                  }`}
                  onClick={handleAddToLibrary}
                  disabled={
                    isInLibraryState || isAddingToLibrary || checkingPlaylist
                  }
                >
                  {isAddingToLibrary ? (
                    ebook?.language?.toLowerCase() === "hindi" ? "लोड हो रहा है..." : "Loading..."
                  ) : checkingPlaylist ? (
                    ebook?.language?.toLowerCase() === "hindi" ? "जांच हो रही है..." : "Checking..."
                  ) : isInLibraryState ? (
                    <>
                      <Check size={18} />
                      {ebook?.language?.toLowerCase() === "hindi" ? "जोड़ा गया" : "Added"}
                    </>
                  ) : (
                    <>
                      <Bookmark size={18} />
                      {ebook?.language?.toLowerCase() === "hindi" ? "लाइब्रेरी में जोड़ें" : "Add to Library"}
                    </>
                  )}
                </button>
              </div>
              
              {(seriesData.description || ebook.description) && (
                <div className={styles.description}>
                  <h3>{ebook?.language?.toLowerCase() === "hindi" ? "विवरण" : "Description"}</h3>
                  <p>{seriesData?.description || ebook.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Only render episodes section if the book has chapters */}
          {hasChapters && (
            <div className={styles.episodesSection}>
              <h2 className={styles.episodesTitle}>
                {ebook?.language?.toLowerCase() === "hindi" ? "एपिसोड" : "Episodes"}
              </h2>
              <div className={styles.episodesGrid}>
                {chapters.map((chapter, index) => (
                  <div 
                    key={chapter.id} 
                    className={styles.episodeCard}
                    onClick={() => handleEpisodeClick(index)}
                  >
                    <div className={styles.episodeImageWrapper}>
                      <img
                        src={
                          chapter.ebook_mobile_cover ||
                          chapter.ebook_computer_cover
                        }
                        alt={chapter.ebook_title}
                        className={styles.episodeImage}
                      />
                    </div>
                    <div className={styles.episodeInfo}>
                      <h3
                        className={styles.episodeName}
                      >{`Episode ${chapter.chapter_number}`}</h3>
                      <p className={styles.episodeSubtitle}>
                        {chapter.ebook_title}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <SimilarBooks id={ebook?.id} />
      </div>
      <Footer />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
        message={getLoginModalMessage()}
      />
    </>
  );
};

export default BookDetails;